import { useState, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useBodyScrollLock } from "@/shared/lib/hooks/useBodyScrollLock"
import { NavbarBrand } from "./NavbarBrand"
import { NavbarPartners } from "./NavbarPartners"
import { NavbarDesktopLinks } from "./NavbarDesktopLinks"
import { NavbarAuthArea } from "./NavbarAuthArea"
import { NavbarMobileMenu } from "./NavbarMobileMenu"

const getEntranceClass = (shouldAnimate: boolean, delayClass: string) =>
  shouldAnimate ? `hero-fade-up ${delayClass}` : ""

export default function Navbar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement | null>(null)
  const entranceDoneRef = useRef(false)

  const isHome = pathname === "/"
  const isAtTop = !scrolled
  const isHomeHero = isHome && isAtTop
  const headerTone = isHomeHero ? "transparent" : "solid"
  const shouldAnimateEntrance = isHome && !entranceDoneRef.current

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60)
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (!isHome || entranceDoneRef.current) return
    const timer = window.setTimeout(() => {
      entranceDoneRef.current = true
    }, 750)
    return () => window.clearTimeout(timer)
  }, [isHome])

  useBodyScrollLock(mobileOpen)

  useEffect(() => {
    if (!mobileOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        setMobileOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [mobileOpen])

  const navShellClass = isAtTop
    ? isHomeHero
      ? "bg-transparent border-b border-white/15"
      : "bg-transparent border-b border-transparent"
    : "bg-lib-card/95 backdrop-blur-md shadow-md shadow-lib-primary/5 border-b border-lib-border"

  const headerHeightClass = scrolled ? "h-14 md:h-16" : "h-16 md:h-20"

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navShellClass}`}>
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className={`flex items-center justify-between gap-3 transition-all duration-500 ${headerHeightClass}`}>
            <div
              className={`flex items-center gap-2 sm:gap-2.5 min-w-0 shrink-0 ${getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-0")}`}
            >
              <NavbarBrand transparent={isHomeHero} />
              <span
                className={`shrink-0 select-none text-lg sm:text-xl leading-none font-light ${
                  isHomeHero ? "text-white/45" : "text-lib-text-secondary/50"
                }`}
                aria-hidden
              >
                ×
              </span>
              <NavbarPartners compact={isHome} />
            </div>
            <NavbarDesktopLinks
              headerTone={headerTone}
              ariaLabel={t("nav.mainNav")}
              entranceClass={getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-1")}
            />
            <div
              className={`flex items-center gap-2 ${getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-2")}`}
            >
              <NavbarAuthArea headerTone={headerTone} />
              <button
                ref={menuButtonRef}
                type="button"
                onClick={() => setMobileOpen((open) => !open)}
                className={`xl:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                  isHomeHero ? "text-white hover:bg-white/10" : "text-lib-text hover:bg-lib-primary/5"
                }`}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav-menu"
                aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              >
                <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </nav>
      {mobileOpen ? (
        <NavbarMobileMenu
          id="mobile-nav-menu"
          ariaLabel={t("nav.mainNav")}
          topClass={scrolled ? "top-14 md:top-16" : "top-16 md:top-20"}
          onItemClick={() => setMobileOpen(false)}
        />
      ) : null}
    </>
  )
}
